import SSCarousel from "@core-components/carousel/Carousel";
import InfoArea from "@core-components/info-area/InfoArea";
import SSPagination from "@core-components/pagination/Pagination";
import AppSkeletonBuyCardList from "@core-components/product/component/AppSkeletonBuyCardList";
import SSHeadline from "@core-components/title/SSHeadline";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import type { IPaginationProps } from "@fetcher/models/IPagination";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { EBreakpoints, useBreakpoint } from "@hooks/useBreakpoint";
import { SimpleGrid } from "@mantine/core";
import { useLoaderStore } from "@modules/loader/useLoader.hook";
import { useMemo } from "react";
import type { IRankedSmurfCardItem } from "../models/IRankedSmurf.models";
import SSRankedCard from "./SSRankedCard";

interface IRankedSmurfListProps {
  accounts: IRankedSmurfCardItem[] | undefined;
  onPaginate?: (page: number) => void;
  paginationProps?: IPaginationProps;
}

export const SmurfRankedList: React.FC<IRankedSmurfListProps> = ({
  accounts,
  onPaginate,
  paginationProps,
}) => {
  const { t } = useAppTranslation("pages.ranked.nothingFound");
  const { breakpoint } = useBreakpoint();
  const useLoader = useLoaderStore();
  const accountsCards = useMemo(
    () =>
      accounts?.map((account) => (
        <SSRankedCard key={account.id} account={account} />
      )),
    [accounts],
  );

  const displayAsList = useMemo(
    () => breakpoint >= EBreakpoints.XL,
    [breakpoint],
  );

  if (useLoader.isLoading()) {
    return <AppSkeletonBuyCardList />;
  }

  if (accounts?.length === 0) {
    return (
      <InfoArea
        hasShadow
        className="xs:!p-10 my-20"
        description={<p className="text-[16px]">{t("description")}</p>}
        fullWidth={false}
        headLine={
          <SSHeadline>
            <SSWhiteText>¯\_(ツ)_/¯ {t("title")}</SSWhiteText>
          </SSHeadline>
        }
      />
    );
  }

  return (
    <>
      {displayAsList ? (
        <SimpleGrid
          classNames={{ root: "px-2" }}
          cols={{ base: 1, sm: 2, md: 3, lg: 3, xl: 4 }}
        >
          {accountsCards}
        </SimpleGrid>
      ) : (
        <SSCarousel loop={false}>{accountsCards}</SSCarousel>
      )}
      {accounts && onPaginate && paginationProps && (
        <SSPagination
          currentPage={paginationProps.currentPage}
          itemsCount={paginationProps.total}
          itemsPerPage={paginationProps.perPage}
          pageCount={paginationProps.lastPage}
          onPaginate={onPaginate}
          classNames={{ root: "!px-10 !pt-5" }}
        />
      )}
    </>
  );
};
